import { FC, useEffect, useMemo, useState } from 'react';
import InstaStories from 'react-insta-stories';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/common/Button/Button';
import { ModalBase } from 'src/components/common/modal/ModalBase';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { useBannerActions } from 'src/hooks/useBannerActions';
import { useGameAction } from 'src/hooks/useGameAction';
import { CmsStoryItem } from 'src/types/cms/cmsWidgetTypes';
import { RootState } from 'src/types/store-types';
import './StoriesModal.scss';

const Content: FC<CmsStoryItem & { handleClose: () => void }> = ({
  title,
  subtitle,
  storyImage,
  action,
  handleClose,
}) => {
  const { tags } = useSelector((state: RootState) => state.games);
  const [tagList, setTagList] = useState<any[]>([]);
  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { categoriesData } = useSelector((state: RootState) => state.configs);
  const { providerAction, loginAction, registrAction, promotionAction, categoryAction, externalUrlAction } =
    useBannerActions();

  const { handleActionGame } = useGameAction();

  const callToActionHandler = (): void => {
    handleClose();
    switch (action?.variant) {
      case 'goToRegister':
        registrAction();
        break;
      case 'goToLogin':
        loginAction();
        break;
      case 'goToPromotion':
      case 'goToPromotions':
        promotionAction(action?.param);
        break;
      case 'goToProvider':
        providerAction({ provider: action?.param, tags: tags, tagList: tagList });
        break;
      case 'goToExternalUrl':
        externalUrlAction(action?.param);
        break;
      case 'goToGame':
        handleActionGame(action?.param as number);
        break;
      case 'goToCategory':
        categoryAction(action?.param);
        break;
      case 'goToRace':
      case 'noAction':
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    categoriesData &&
      setTagList(categoriesData[selectedCategory]?.tags?.filter((f) => f.tagName === 'allGames')?.[0].tagGames || []);
  }, [categoriesData]);

  return (
    <div className="StoriesModalContent">
      <div className="StoriesModalHead">
        <span className="StoriesModalTitle">{title}</span>
        <span className="StoriesModalSubTitle">{subtitle}</span>
        <button className="StoriesModalClose">
          <SvgIcon src={icons.close} onClick={handleClose} />
        </button>
      </div>

      <div className="StoriesModalBody">
        <img className="StoriesModalImage" src={storyImage.path} alt={storyImage.original_name} />
      </div>

      {action.variant !== 'noAction' && (
        <div className="StoriesModalFooter">
          <Button
            size="md"
            type="button"
            onClick={() => callToActionHandler()}
            className="StoriesModalFooterButton primary-filled-btn"
          >
            {action.label}
          </Button>
        </div>
      )}
    </div>
  );
};

export const StoriesModal: FC<{
  open: boolean;
  stories: CmsStoryItem[];
  groupTitle?: string;
  onClose: () => void;
  onAllStoriesEnd: (groupTitle?: string) => void;
}> = ({ open, stories, groupTitle, onClose, onAllStoriesEnd }) => {
  const mappedStories = useMemo(
    () =>
      stories.map((story) => ({
        content: () => (
          <Content
            action={story!.action}
            title={story!.title}
            subtitle={story!.subtitle}
            storyImage={story!.storyImage}
            handleClose={onClose}
          />
        ),
      })),
    [stories]
  );

  const handleAllStoriesEnd = (): void => {
    onAllStoriesEnd(groupTitle);
    onClose();
  };

  return (
    <ModalBase open={open} placement={'center'}>
      <div className="StoriesModal">
        {!!stories.length && (
          <InstaStories
            width="100%"
            height="100%"
            keyboardNavigation
            onAllStoriesEnd={handleAllStoriesEnd}
            stories={mappedStories}
          />
        )}
      </div>
    </ModalBase>
  );
};
